import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  BarChart3, TrendingDown, Sparkles, ShieldCheck, Users2, CalendarDays, Ticket,
} from "lucide-react";
import { fmtDate } from "@/lib/format";

/**
 * Audition Analytics dialog.
 * Renders a one-glance director's view: signup volume over time, age bands,
 * the drop-off funnel from signup → cast, plus slot utilisation and minor
 * consent hygiene. All plain SVG — no chart library, no per-cell hover to
 * keep the dependency footprint tiny.
 */
export default function AuditionAnalytics({ open, onOpenChange, productionId, productionTitle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !productionId) return;
    setLoading(true);
    api.get("/auditions/analytics", { params: { production_id: productionId } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load analytics"))
      .finally(() => setLoading(false));
  }, [open, productionId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="audition-analytics-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-gold" /> Audition Analytics
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {productionTitle ? <>Casting insights for <span className="text-zinc-300">{productionTitle}</span>.</> : "Casting insights for this production."}
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="text-sm text-zinc-500 py-16 text-center">Crunching numbers…</div>
        ) : data.total === 0 ? (
          <div className="text-sm text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-md">
            No auditions submitted yet — analytics will appear once the first signup arrives.
          </div>
        ) : (
          <div className="space-y-6" data-testid="audition-analytics-body">
            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi icon={Users2}     label="Total signups"  value={data.total} testid="kpi-total" />
              <Kpi icon={Sparkles}   label="Virtual"        value={data.virtual_split.virtual}
                   sub={`${data.virtual_split.in_person} in-person`} testid="kpi-virtual" />
              <Kpi icon={Ticket}     label="Slots filled"   value={data.slot_utilization.filled}
                   sub={data.slot_utilization.capacity ? `of ${data.slot_utilization.capacity} capacity` : "no slots yet"} testid="kpi-slots" />
              <Kpi icon={ShieldCheck} label="Minors (consented)"
                   value={`${data.minor_stats.with_consent}/${data.minor_stats.minors}`}
                   sub={data.minor_stats.minors === data.minor_stats.with_consent ? "All consents on file" : "Missing consent!"}
                   accent={data.minor_stats.minors !== data.minor_stats.with_consent ? "amber" : "default"}
                   testid="kpi-minors" />
            </div>

            {/* Funnel */}
            <Card title="Drop-off funnel" icon={TrendingDown} testid="analytics-funnel">
              <Funnel funnel={data.funnel} />
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Signups per day */}
              <Card title="Signups per day" icon={CalendarDays} testid="analytics-by-day">
                <DayBars series={data.by_day} />
              </Card>

              {/* Age bands */}
              <Card title="Age bands" icon={Users2} testid="analytics-by-age">
                <AgeBars bands={data.by_age} total={data.total} />
              </Card>
            </div>

            {/* Top roles */}
            {data.top_roles.length > 0 && (
              <Card title="Most-requested roles" icon={Sparkles} testid="analytics-top-roles">
                <ol className="space-y-1.5">
                  {data.top_roles.map((r, i) => (
                    <li key={r.role} className="flex items-center gap-3 text-sm">
                      <span className="text-[11px] text-zinc-600 font-mono w-5 text-right">{i + 1}.</span>
                      <span className="flex-1 text-zinc-200 truncate">{r.role}</span>
                      <span className="text-gold font-mono text-xs">{r.count}</span>
                      <span className="w-32 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <span
                          className="block h-full bg-gold/70"
                          style={{ width: `${(r.count / data.top_roles[0].count) * 100}%` }}
                        />
                      </span>
                    </li>
                  ))}
                </ol>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- little chart building blocks ---------- */

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  const border = accent === "amber" ? "border-amber-500/40" : "border-white/10";
  const glow = accent === "amber" ? "text-amber-300" : "text-gold";
  return (
    <div data-testid={testid} className={`rounded-lg border ${border} bg-[#121212] p-4`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
        <Icon className={`w-3.5 h-3.5 ${glow}`} /> {label}
      </div>
      <div className={`mt-2 font-display text-3xl ${glow}`}>{value}</div>
      {sub && <div className={`text-[11px] mt-1 ${accent === "amber" ? "text-amber-400/80" : "text-zinc-500"}`}>{sub}</div>}
    </div>
  );
}

function Card({ title, icon: Icon, testid, children }) {
  return (
    <section data-testid={testid} className="rounded-lg border border-white/10 bg-[#121212] p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
        {Icon && <Icon className="w-3.5 h-3.5 text-gold" />} {title}
      </div>
      {children}
    </section>
  );
}

function Funnel({ funnel }) {
  const top = funnel[0]?.count || 1;
  return (
    <div className="space-y-2">
      {funnel.map((f, i) => {
        const prev = funnel[i - 1]?.count ?? f.count;
        const drop = prev && prev > f.count ? Math.round(((prev - f.count) / prev) * 100) : 0;
        const pct = Math.max(4, (f.count / top) * 100);
        return (
          <div key={f.stage} data-testid={`funnel-${f.stage}`} className="flex items-center gap-3 text-sm">
            <span className="w-32 text-zinc-300 text-xs truncate">{f.label}</span>
            <span className="flex-1 h-6 rounded bg-white/5 relative overflow-hidden">
              <span className="absolute inset-y-0 left-0 bg-gold/70" style={{ width: `${pct}%` }} />
              <span className="absolute inset-0 flex items-center px-2 text-xs font-mono text-black mix-blend-screen">
                {f.count}
              </span>
            </span>
            <span className={`w-16 text-right text-[11px] font-mono ${drop > 30 ? "text-red-300" : "text-zinc-500"}`}>
              {i === 0 ? "—" : drop ? `-${drop}%` : "—"}
            </span>
          </div>
        );
      })}
      <div className="text-[11px] text-zinc-500 pt-1">
        Percentages show drop-off from the previous stage.
      </div>
    </div>
  );
}

function DayBars({ series }) {
  const max = useMemo(() => series.reduce((m, s) => Math.max(m, s.count), 0), [series]);
  if (series.length === 0) {
    return <div className="text-xs text-zinc-500 py-6 text-center">No dated signups yet.</div>;
  }
  return (
    <div>
      <div className="flex items-end gap-1 h-32">
        {series.map((s) => {
          const h = Math.max(4, (s.count / (max || 1)) * 100);
          return (
            <div key={s.date} className="flex-1 flex flex-col items-center justify-end group" title={`${s.date}: ${s.count}`}>
              <span className="text-[9px] text-zinc-500 font-mono">{s.count}</span>
              <span
                className="w-full rounded-t bg-gold/70 group-hover:bg-gold transition-colors"
                style={{ height: `${h}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-1 mt-2">
        {series.map((s) => (
          <div key={s.date} className="flex-1 text-[9px] text-zinc-500 text-center truncate">
            {fmtDate(s.date, { short: true }) || s.date.slice(5)}
          </div>
        ))}
      </div>
    </div>
  );
}

function AgeBars({ bands, total }) {
  return (
    <div className="space-y-2">
      {bands.map((b) => {
        const pct = total ? (b.count / total) * 100 : 0;
        const isUnder18 = b.key === "under_18";
        return (
          <div key={b.key} data-testid={`age-band-${b.key}`} className="flex items-center gap-3 text-sm">
            <span className="w-20 text-zinc-300 text-xs">{b.label}</span>
            <span className="flex-1 h-4 rounded-full bg-white/5 overflow-hidden">
              <span
                className={`block h-full ${isUnder18 ? "bg-amber-400/80" : "bg-gold/70"}`}
                style={{ width: `${pct}%` }}
              />
            </span>
            <span className="w-16 text-right text-[11px] font-mono text-zinc-400">
              {b.count} <span className="text-zinc-600">· {pct.toFixed(0)}%</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
